import isValidDomain from "is-valid-domain";
import isEmail from "validator/lib/isEmail";
import { ACORN_BRAND_FEATURE_LABEL } from "./logFields";
import type { GrapevineBrandFields } from "./grapevineBrandForm";

/** Local part of an alias email, normalized for Grapevine slug (e.g. lift@ne.com → lift). */
export function slugFromAliasEmail(alias: string): string {
  const local = alias.trim().split("@")[0] ?? "";
  return local
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Checkbox products on the main add-brand form (excludes Performance Creator). */
export type WizardProductFeature =
  | "Affiliate"
  | "Performance"
  | "Prospect"
  | "Creative"
  | "Acorn";

export const WIZARD_PRODUCT_FEATURES: {
  key: WizardProductFeature;
  label: string;
}[] = [
  { key: "Affiliate", label: "Affiliate" },
  { key: "Performance", label: "Performance" },
  { key: "Prospect", label: "Prospect" },
  { key: "Creative", label: "Creative" },
  { key: "Acorn", label: ACORN_BRAND_FEATURE_LABEL },
];

export type AddBrandWizardResult = {
  organizationId: string;
  organizationLabel: string;
  name: string;
  alias: string;
  domain: string;
  vertical: string[];
  type: string;
  productFeatures: WizardProductFeature[];
  isPerformanceCreator: boolean;
  hubspotCompanyId: string;
  gvSlug: string;
  gvVertical: string;
  gvContactName: string;
  gvContactEmail: string;
  gvHomepageUrl: string;
  gvTier: string;
};

export function hasAtLeastOneBrandFeature(
  productFeatures: WizardProductFeature[],
  isPerformanceCreator: boolean,
): boolean {
  return productFeatures.length > 0 || isPerformanceCreator;
}

export type WizardStep2Fields = {
  name: string;
  alias: string;
  domain: string;
  vertical: string[];
  type: string;
};

export type WizardStep2Errors = Partial<
  Record<"name" | "alias" | "vertical" | "type" | "domain", string>
>;

/** Per-field errors, matching the main add-brand form validation for core fields. */
export function getWizardStep2Errors(
  fields: WizardStep2Fields,
): WizardStep2Errors {
  const errors: WizardStep2Errors = {};
  if (!fields.name.trim()) {
    errors.name = "Name of brand is required!";
  }
  if (!fields.alias.trim() || !isEmail(fields.alias.trim())) {
    errors.alias = "Alias must be an email!!";
  }
  if (!fields.vertical.length) {
    errors.vertical = "Vertical of brand is required!";
  }
  if (!fields.type) {
    errors.type = "Program type of brand is required!";
  }
  const domain = fields.domain.trim();
  if (!domain) {
    errors.domain = "Domain of brand is required!";
  } else if (
    !isValidDomain(domain) ||
    domain.split(":")[0] === "http" ||
    domain.split(":")[0] === "https"
  ) {
    errors.domain = "Domain of brand should be of format eg: domain_name.com";
  }
  return errors;
}

const WIZARD_STEP2_ORDER: (keyof WizardStep2Errors)[] = [
  "name",
  "alias",
  "vertical",
  "type",
  "domain",
];

/** First step 2 error in form order, or null when valid. */
export function validateWizardStep2(fields: WizardStep2Fields): string | null {
  const errors = getWizardStep2Errors(fields);
  const first = WIZARD_STEP2_ORDER.find((key) => errors[key]);
  return first ? (errors[first] as string) : null;
}

export type WizardStep3Errors = Partial<
  Record<
    "features" | "hubspotCompanyId" | "gvVertical" | "gvContactEmail" | "slug",
    string
  >
>;

/** Per-field errors for step 3 (brand features + Grapevine fields). */
export function getWizardStep3Errors(
  productFeatures: WizardProductFeature[],
  isPerformanceCreator: boolean,
  hubspotCompanyId: string,
  grapevine: GrapevineBrandFields,
): WizardStep3Errors {
  const errors: WizardStep3Errors = {};
  if (!hasAtLeastOneBrandFeature(productFeatures, isPerformanceCreator)) {
    errors.features = "Select at least one brand feature";
  }
  if (!isPerformanceCreator) return errors;

  if (!hubspotCompanyId.trim()) {
    errors.hubspotCompanyId =
      "HubSpot Company ID is required for Performance Creator brands";
  }
  if (!grapevine.vertical.trim()) {
    errors.gvVertical =
      "Grapevine vertical is required for Performance Creator brands";
  }
  if (!grapevine.contactEmail.trim() || !isEmail(grapevine.contactEmail)) {
    errors.gvContactEmail =
      "A valid Grapevine contact email is required for Performance Creator brands";
  }
  if (!grapevine.slug.trim()) {
    errors.slug = "Grapevine slug is required for Performance Creator brands";
  }
  return errors;
}

const WIZARD_STEP3_ORDER: (keyof WizardStep3Errors)[] = [
  "features",
  "hubspotCompanyId",
  "gvVertical",
  "gvContactEmail",
  "slug",
];

/** First step 3 error in form order, or null when valid. */
export function validateWizardStep3(
  productFeatures: WizardProductFeature[],
  isPerformanceCreator: boolean,
  hubspotCompanyId: string,
  grapevine: GrapevineBrandFields,
): string | null {
  const errors = getWizardStep3Errors(
    productFeatures,
    isPerformanceCreator,
    hubspotCompanyId,
    grapevine,
  );
  const first = WIZARD_STEP3_ORDER.find((key) => errors[key]);
  return first ? (errors[first] as string) : null;
}

/** Parse organization id from POST /organizations response (shape varies by API version). */
export function parseCreatedOrganizationId(data: unknown): number | null {
  const body = data as Record<string, unknown> | null | undefined;
  const response = body?.response;
  const candidates: unknown[] = [
    typeof response === "object" && response !== null
      ? (response as Record<string, unknown>).id
      : undefined,
    body?.id,
  ];
  for (const candidate of candidates) {
    const id = typeof candidate === "number" ? candidate : Number(candidate);
    if (Number.isInteger(id) && id > 0) return id;
  }
  return null;
}

export type WizardOrgOption = {
  value: number;
  label: string;
  is_active: boolean;
};

/** Prefer highest id when multiple orgs share the same name (newest create). */
export function findOrganizationIdByName(
  orgs: WizardOrgOption[],
  name: string,
): number | null {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return null;
  const matches = orgs.filter(
    (o) => o.is_active && o.label.trim().toLowerCase() === normalized,
  );
  if (!matches.length) return null;
  return Math.max(...matches.map((o) => o.value));
}
